import { useEffect, useReducer, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { SITE } from '@/constants/testIds';
import { Stardust } from '@/components/site/Logo';
import { QUOTE_MAILTO } from '@/constants/contact';

const CLEAN_BG = '/images/denver-hero.jpg';

const WIPE_DURATION = 1.9; // seconds
const START_DELAY = 900; // ms — let the water linger a moment

export function SqueegeeHero() {
  const reduceMotion = useReducedMotion();

  // Phases: idle → wiping → done
  const [started, setStarted] = useState(false);
  const [cleared, setCleared] = useState(false);

  useEffect(() => {
    if (reduceMotion) {
      setStarted(true);
      setCleared(true);
      return;
    }
    const t1 = setTimeout(() => setStarted(true), START_DELAY);
    const t2 = setTimeout(
      () => setCleared(true),
      START_DELAY + WIPE_DURATION * 1000 + 100,
    );
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [reduceMotion]);

  return (
    <section
      id="top"
      data-testid={SITE.heroSection}
      className="relative w-full h-[100svh] min-h-[640px] overflow-hidden bg-background"
      style={{ position: 'relative' }}
    >
      {/* LAYER 1 — Denver skyline behind the glass */}
      <div className="absolute inset-0">
        <img
          src={CLEAN_BG}
          alt="Denver skyline viewed through a clean window"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 glass-tint" />
        <div className="absolute inset-0 hero-scrim" />
      </div>

      {/* LAYER 2 — Hero content (always mounted, revealed as water clips away) */}
      <div className="absolute inset-0 z-10 flex flex-col justify-between pointer-events-none">
        <TopBar />

        <div className="px-6 lg:px-14 pointer-events-auto">
          <div className="max-w-[1400px] mx-auto grid grid-cols-12 gap-6 items-end pb-24 lg:pb-32">
            <div className="col-span-12 lg:col-span-8 relative">
              <motion.span
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: cleared ? 1 : 0, y: cleared ? 0 : 8 }}
                transition={{ duration: 0.7, delay: 0.1 }}
                className="label text-white/85 inline-block relative"
              >
                Colorado Front Range · Window Care · est. 2024
              </motion.span>
              <h1
                data-testid={SITE.heroTitle}
                className="font-heading font-light uppercase tracking-[-0.03em] leading-[0.88] text-[13vw] sm:text-[11vw] lg:text-[9.4vw] mt-6 text-white hero-text-shadow relative"
              >
                <span className="block">Clarity,</span>
                <span className="font-serif italic lowercase font-light tracking-tight block">
                  restored.
                </span>
                <Stardust className="opacity-90" />
              </h1>
            </div>

            <div className="col-span-12 lg:col-span-4 lg:pb-4">
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: cleared ? 1 : 0, y: cleared ? 0 : 10 }}
                transition={{ duration: 0.7, delay: 0.25 }}
                className="relative isolate"
              >
                {/* Legibility scrim behind the paragraph + CTAs */}
                <div
                  aria-hidden
                  className="absolute -inset-x-4 -inset-y-4 lg:-inset-x-6 lg:-inset-y-6 -z-10 bg-[rgba(15,18,16,0.55)] backdrop-blur-[3px]"
                />
                <p
                  data-testid={SITE.heroSubtitle}
                  className="text-white text-base lg:text-lg leading-relaxed font-light max-w-md hero-text-shadow"
                >
                  Premium residential and commercial window washing for
                  Colorado homes and buildings that expect a spotless,
                  streak-free finish — every pane, every time.
                </p>
                <div className="mt-8 flex flex-wrap items-center gap-3">
                  <a
                    data-testid={SITE.heroCtaPrimary}
                    href={QUOTE_MAILTO}
                    className="inline-flex items-center gap-3 bg-white text-foreground px-6 py-4 text-[12px] tracking-[0.22em] uppercase font-medium hover:bg-[hsl(var(--gold))] hover:text-foreground transition-colors shadow-lg shadow-black/30"
                  >
                    Book a clean
                    <span aria-hidden>→</span>
                  </a>
                  <a
                    data-testid={SITE.heroCtaSecondary}
                    href={QUOTE_MAILTO}
                    className="inline-flex items-center gap-3 border border-white/70 text-white px-6 py-4 text-[12px] tracking-[0.22em] uppercase font-medium hover:bg-white hover:text-foreground transition-colors backdrop-blur-sm"
                  >
                    Free Quote
                  </a>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      {/* LAYER 3 — Gold squeegee travels across (BEHIND the water — visible through it) */}
      <motion.div
        aria-hidden
        data-testid={SITE.heroSqueegee}
        className="absolute top-0 bottom-0 z-20 pointer-events-none flex items-center"
        style={{ width: 'clamp(120px, 12vw, 200px)', left: 0 }}
        initial={{ x: '-100%' }}
        animate={
          started ? { x: 'calc(100vw + 20px)' } : { x: '-100%' }
        }
        transition={{ duration: WIPE_DURATION, ease: [0.72, 0, 0.28, 1] }}
      >
        <GoldSqueegee />
      </motion.div>

      {/* LAYER 4 — Water film (in front of squeegee; clips away left→right) */}
      <motion.div
        aria-hidden
        className="absolute inset-0 z-30 water-film"
        initial={{ clipPath: 'inset(0 0% 0 0%)' }}
        animate={started ? { clipPath: 'inset(0 0% 0 108%)' } : {}}
        transition={{ duration: WIPE_DURATION, ease: [0.72, 0, 0.28, 1] }}
      >
        <div className="absolute inset-0 water-blur" />
        <div className="absolute inset-0 water-tint" />
        <div className="absolute inset-0 water-droplets" />
        <div className="absolute inset-0 water-noise" />
      </motion.div>

      {/* Bottom vignette — subtle fade to next section */}
      <div className="absolute inset-x-0 bottom-0 h-24 z-[5] bg-gradient-to-t from-background via-background/60 to-transparent" />
    </section>
  );
}

function TopBar() {
  return <div className="pt-24 lg:pt-28" />;
}

function GoldSqueegee() {
  return (
    <div className="relative w-full h-full flex items-center justify-end">
      {/* Ambient glow */}
      <div className="absolute inset-0 opacity-70 pointer-events-none"
        style={{
          background:
            'radial-gradient(60% 40% at 55% 50%, rgba(212,178,90,0.35) 0%, rgba(212,178,90,0) 70%)',
          mixBlendMode: 'screen',
        }}
      />

      {/* Handle (horizontal, sticks out to the left of the blade) */}
      <div className="absolute right-[14%] top-1/2 -translate-y-1/2 flex items-center gap-1.5">
        <div className="w-3 h-4 rounded-[2px] bg-[linear-gradient(180deg,#F6E6A5,#8C6A24)] shadow-[inset_0_1px_0_rgba(255,255,255,0.75),0_2px_6px_rgba(0,0,0,0.35)]" />
        <div className="relative w-[clamp(60px,7vw,110px)] h-[10px] rounded-[3px]"
          style={{
            background:
              'linear-gradient(180deg,#8C6A24 0%,#F6E6A5 35%,#D4B25A 55%,#8C6A24 100%)',
            boxShadow:
              'inset 0 1px 0 rgba(255,255,255,0.7), inset 0 -1px 0 rgba(0,0,0,0.35), 0 3px 10px rgba(0,0,0,0.35)',
          }}
        >
          <div className="absolute inset-y-0 left-2 right-2 top-[3px] h-[2px] bg-white/70 mix-blend-screen" />
        </div>
      </div>

      {/* Blade body (vertical gold bar, full pane height) */}
      <div className="relative h-[92%] flex items-stretch">
        <div
          className="w-[10px] relative"
          style={{
            background:
              'linear-gradient(90deg,#8C6A24 0%,#F6E6A5 40%,#D4B25A 60%,#8C6A24 100%)',
            boxShadow:
              'inset 1px 0 0 rgba(255,255,255,0.7), inset -1px 0 0 rgba(0,0,0,0.4), 0 6px 24px rgba(212,178,90,0.35)',
          }}
        >
          {/* travelling shine */}
          <motion.div
            className="absolute left-[4px] right-[4px] h-[24%] bg-white/85 mix-blend-screen blur-[1px]"
            initial={{ top: '-30%' }}
            animate={{ top: ['-30%', '110%'] }}
            transition={{
              duration: 1.6,
              repeat: Infinity,
              repeatDelay: 1.2,
              ease: 'easeInOut',
            }}
          />
        </div>
        {/* rubber blade — leading edge on the right */}
        <div
          className="w-[3px]"
          style={{
            background: 'linear-gradient(90deg,#1a1c1a 0%,#050605 100%)',
            boxShadow: 'inset 1px 0 0 rgba(255,255,255,0.15)',
          }}
        />
        {/* wet trail just ahead of the blade (right of it, where water still exists) */}
        <div
          className="absolute top-0 bottom-0 left-full w-[3px] bg-white/80 mix-blend-screen"
          style={{ filter: 'blur(1px)' }}
        />
      </div>
    </div>
  );
}
